import { Component, signal, OnDestroy } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Offline Indicator Component
 *
 * Displays a banner at the top of the screen when the user is offline.
 * Automatically detects online/offline state changes.
 *
 * @example
 * ```html
 * <app-offline-indicator />
 * ```
 */
@Component({
  selector: 'app-offline-indicator',
  imports: [TranslateModule],
  template: `
    @if (!isOnline()) {
      <div class="offline-banner">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <span>{{ 'OFFLINE.MESSAGE' | translate }}</span>
      </div>
    }
  `,
  styles: [`
    .offline-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 9999;
      background: var(--p-orange-500);
      color: white;
      padding: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      font-weight: 500;

      i {
        font-size: 1.25rem;
      }
    }
  `]
})
export class OfflineIndicatorComponent implements OnDestroy {
  protected readonly isOnline = signal(navigator.onLine);

  private onlineHandler = () => this.isOnline.set(true);
  private offlineHandler = () => this.isOnline.set(false);

  constructor() {
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
  }
}
